import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { apiService } from '../services/api';
import { AssuntoMonitoringActivationLogList } from './AssuntoMonitoringActivationLogList';
import { ChangeEmailDeliveryLogList } from './ChangeEmailDeliveryLogList';

interface ChangeDetailLogsPanelProps {
  assuntoKey: string | null;
  assuntoLabel: string | null;
  changeId: number | null | undefined;
  isMonitored: boolean;
  enabled?: boolean;
}

export function ChangeDetailLogsPanel({
  assuntoKey,
  assuntoLabel,
  changeId,
  isMonitored,
  enabled = true,
}: ChangeDetailLogsPanelProps) {
  const [activeTab, setActiveTab] = useState(0);

  const showAssuntoTab = !!assuntoKey;
  const showEmailTab = isMonitored && !!changeId;

  const { data: assuntoData, isLoading: assuntoLoading } = useQuery(
    ['assunto-history-detail', assuntoKey],
    () => apiService.getAssuntoMonitoringHistoryByKey(assuntoKey!),
    { enabled: enabled && !!assuntoKey, refetchOnWindowFocus: false },
  );

  const { data: emailLogs, isLoading: emailLoading } = useQuery(
    ['change-delivery-logs', changeId],
    () => apiService.getChangeDeliveryLogs(changeId!),
    { enabled: enabled && showEmailTab, refetchOnWindowFocus: false },
  );

  const assuntoHistory = assuntoData?.history ?? [];
  const emailCount = emailLogs?.length ?? 0;

  if (!showAssuntoTab && !showEmailTab) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="text.secondary">
          Nenhum log de monitoramento ou envio de e-mail para esta mudança.
        </Typography>
      </Box>
    );
  }

  const tabLabels: Array<{ key: 'assunto' | 'email'; label: string }> = [];
  if (showAssuntoTab) {
    tabLabels.push({ key: 'assunto', label: `Monitoramento do assunto (${assuntoHistory.length})` });
  }
  if (showEmailTab) {
    tabLabels.push({ key: 'email', label: `E-mails (${emailCount})` });
  }

  const currentKey = tabLabels[activeTab]?.key ?? tabLabels[0]?.key;

  return (
    <Box>
      {tabLabels.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            aria-label="Abas de logs da mudança legislativa"
          >
            {tabLabels.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Box>
      )}

      {currentKey === 'assunto' && (
        <AssuntoMonitoringActivationLogList
          history={assuntoHistory}
          loading={assuntoLoading}
          isMonitored={assuntoData?.is_monitored}
          assuntoLabel={assuntoLabel ?? assuntoData?.assunto_label}
        />
      )}

      {currentKey === 'email' && (
        <ChangeEmailDeliveryLogList logs={emailLogs ?? []} loading={emailLoading} />
      )}
    </Box>
  );
}
